import { isEmptyValue, labelForField, parseFileEntries, resolveWidgetKind } from './helpers';
import type { FormField, FormGroup, FormStateValues } from './types';

const isISODate = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(new Date(v).getTime());

const isISODateTime = (v: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(v)) return false;
  return !Number.isNaN(new Date(v).getTime());
};

const todayISO = (): string => new Date().toISOString().slice(0, 10);

const isPastDate = (iso: string): boolean => iso <= todayISO();
const isFutureDate = (iso: string): boolean => iso >= todayISO();

/**
 * Validate one field against the rules from `Form-builder.md` §6.
 * Returns an error message or `null`.
 */
export const validateField = (field: FormField, value: unknown): string | null => {
  const label = labelForField(field);
  const required = !!field.rqd;
  const empty = isEmptyValue(value);
  const constraints = field.constraints || {};
  const kind = resolveWidgetKind(field);

  if (required && empty) return `${label} is required`;
  if (empty) return null; // Non-required + empty → nothing more to check.

  switch (kind) {
    case 'SingleLineText': {
      const s = String(value);
      if (typeof constraints.max_length === 'number' && s.length > constraints.max_length) {
        return `${label} must be at most ${constraints.max_length} characters`;
      }
      if (typeof constraints.regex === 'string') {
        try {
          const re = new RegExp(constraints.regex);
          if (!re.test(s)) return `${label} format is invalid`;
        } catch {
          // Bad regex from metadata — skip silently rather than blocking the user.
        }
      }
      return null;
    }
    case 'Number':
    case 'Decimal':
    case 'counter': {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(n)) return `${label} must be a valid number`;
      if (kind === 'Number' && !Number.isInteger(n)) return `${label} must be a whole number`;
      if (typeof constraints.min_value === 'number' && n < constraints.min_value) {
        return `${label} must be at least ${constraints.min_value}`;
      }
      if (typeof constraints.max_value === 'number' && n > constraints.max_value) {
        return `${label} must be at most ${constraints.max_value}`;
      }
      return null;
    }
    case 'Date': {
      const s = String(value);
      if (!isISODate(s)) return `${label} must be a valid date`;
      if (constraints.isPast && !isPastDate(s)) return `${label} must be today or in the past`;
      if (constraints.isFuture && !isFutureDate(s)) return `${label} must be today or in the future`;
      return null;
    }
    case 'datetime': {
      const s = String(value);
      if (!isISODateTime(s)) return `${label} must be a valid date and time`;
      const dayPart = s.slice(0, 10);
      if (constraints.isPast && !isPastDate(dayPart)) return `${label} must be today or earlier`;
      if (constraints.isFuture && !isFutureDate(dayPart)) return `${label} must be today or later`;
      return null;
    }
    case 'week_picker': {
      const s = String(value);
      if (!/^\d{4}-W(0[1-9]|[1-4][0-9]|5[0-3])$/.test(s)) return `${label} must be a valid ISO week`;
      return null;
    }
    case 'year_picker': {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isInteger(n) || n < 1900 || n > 2100) return `${label} must be a valid year`;
      return null;
    }
    case 'time_interval_picker': {
      const s = String(value);
      const m = /^(\d{2}:\d{2})-(\d{2}:\d{2})$/.exec(s);
      if (!m) return `${label} must be a valid time range`;
      const [, start, end] = m;
      if (start > end) return `${label}: end time must be after start time`;
      return null;
    }
    case 'SingleSelect': {
      const allowed = (field.colOptions || []).map((o) => o.title);
      if (!allowed.includes(String(value))) return `${label}: please choose a valid option`;
      return null;
    }
    case 'MultiSelect': {
      const allowed = new Set((field.colOptions || []).map((o) => o.title));
      const selected = String(value)
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      for (const t of selected) {
        if (!allowed.has(t)) return `${label}: "${t}" is not a valid option`;
      }
      return null;
    }
    case 'Checkbox': {
      if (typeof value !== 'boolean') return `${label} must be true or false`;
      return null;
    }
    case 'geo_code': {
      const v = value as { latitude?: number; longitude?: number } | null;
      if (!v || typeof v.latitude !== 'number' || typeof v.longitude !== 'number') {
        return `${label}: location not captured`;
      }
      return null;
    }
    case 'versioned_image_picker':
    case 'versioned_pdf_picker': {
      const entries = parseFileEntries(value);
      if (entries.length === 0) {
        return required ? `${label} is required` : null;
      }
      const failed = entries.find((e) => e.error);
      if (failed) return `${label}: one or more files failed to upload — remove or retry them before submitting`;
      const max = typeof constraints.max_length === 'number' ? constraints.max_length : 10;
      if (entries.length > max) return `${label}: at most ${max} file${max === 1 ? '' : 's'} allowed`;
      if (constraints.allow_multiple === false && entries.length > 1) {
        return `${label}: only one file is allowed`;
      }
      return null;
    }
    case 'Search':
    case 'location':
    case 'unsupported':
    default:
      return null;
  }
};

/**
 * Walk every visible field and produce a `{ [title]: errorMessage }` map.
 * Empty map means the form is valid.
 */
export const validateForm = (
  groups: FormGroup[],
  values: FormStateValues,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  for (const group of groups) {
    for (const field of group.items) {
      const err = validateField(field, values[field.title]);
      if (err) errors[field.title] = err;
    }
  }
  return errors;
};
